import { useState } from "react";
import { X, Ticket, Loader2, CheckCircle2 } from "lucide-react";
import logo from "../../assets/Revibelogo.png";
import { getAuthToken } from "../../api/auth";

const API_BASE = process.env.REACT_APP_API_URL;

const PromoCodeModal = ({
  isOpen,
  onClose,
  promoCode,
  setPromoCode,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);

  if (!isOpen) return null;

  const handleApply = async () => {
    const trimmed = promoCode.trim();
    if (!trimmed) {
      setError("Please enter a promo code.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const token = getAuthToken();

      const response = await fetch(`${API_BASE}promocode/apply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ promocode: trimmed }),
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        setError(data.error || "Failed to apply promo code. Please try again.");
        return;
      }

      setPromoApplied(true);
      onSuccess?.(data);

      // ADD THIS LINE:
      if (data.userSubscription) {
        localStorage.setItem(
          "userSubscription",
          JSON.stringify(data.userSubscription),
        );
      }

      setTimeout(() => {
        setPromoApplied(false);
        setPromoCode("");
        onClose();
        window.location.reload(); // navigate("/dashboard") ની જગ્યા આ
      }, 1000);
    } catch (err) {
      console.error("[PromoCodeModal] Network error:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setError("");
    setPromoApplied(false);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: "rgba(0,0,0,0.75)" }}
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-sm rounded-3xl p-8 bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          disabled={loading}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition disabled:opacity-40"
        >
          <X size={22} />
        </button>

        <div className="flex items-center justify-center gap-2 mb-6">
          <img src={logo} alt="Revibe logo" className="h-9" />
        </div>

        <h2 className="text-lime-500 text-xl font-bold text-center mb-1">
          Have a Promo Code?
        </h2>
        <p className="text-gray-500 text-sm text-center mb-6">
          Unlock premium features instantly
        </p>

        <div
          className="flex items-center gap-3 rounded-2xl px-4 py-3 mb-2"
          style={{
            border: `1.5px solid ${error ? "#f87171" : "#d1d5db"}`,
            backgroundColor: "#f9fafb",
          }}
        >
          <Ticket size={20} className="text-gray-400 shrink-0" />
          <input
            type="text"
            placeholder="Enter promocode"
            value={promoCode}
            onChange={(e) => {
              setPromoCode(e.target.value);
              if (error) setError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && !loading && handleApply()}
            disabled={loading || promoApplied}
            className="flex-1 bg-transparent text-gray-800 placeholder-gray-400 text-base focus:outline-none disabled:opacity-60"
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        {error && (
          <p className="text-red-500 text-xs text-center mb-3 px-1">{error}</p>
        )}

        {promoApplied && !error && (
          <p className="text-green-500 text-sm text-center mb-3 font-semibold flex items-center justify-center gap-1">
            <CheckCircle2 size={16} />
            Promo code applied successfully!
          </p>
        )}

        {!error && !promoApplied && <div className="mb-3" />}

        <button
          onClick={handleApply}
          disabled={loading || promoApplied}
          className="w-full py-4 bg-lime-400 hover:bg-lime-500 text-black font-bold rounded-full transition duration-300 mb-4 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Applying…
            </>
          ) : (
            "Apply Promocode"
          )}
        </button>

        <button
          onClick={handleClose}
          disabled={loading}
          className="w-full text-center text-gray-500 text-sm hover:text-gray-700 transition disabled:opacity-40"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
};

export default PromoCodeModal;
